// Based on https://github.com/vercel/swr/blob/33954d7b661c70ed25246b413ccb8c98efd4ad8c/_internal/utils/web-preset.ts

// MIT License

// Copyright (c) 2023 Vercel, Inc.

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

/**
 * Due to the bug https://bugs.chromium.org/p/chromium/issues/detail?id=678075,
 * it's not reliable to detect if the browser is currently online or offline
 * based on `navigator.onLine`.
 * As a workaround, we always assume it's online on the first load, and change
 * the status upon `online` or `offline` events.
 */
let online = true;
export const isOnline = () => online;

const noop = () => {};

// For node and React Native, `add/removeEventListener` doesn't exist on window.
const [onWindowEvent, offWindowEvent] =
  window && window.addEventListener
    ? [
        window.addEventListener.bind(window),
        window.removeEventListener.bind(window),
      ]
    : [noop, noop];

/**
 * Waits until the browser reports it is back online, giving up after
 * `timeoutMilliseconds`. Resolves immediately when already online.
 *
 * This matters when a device wakes from sleep: the app resumes before the
 * network stack is ready, so a request sent straight away fails for reasons
 * that have nothing to do with the request itself.
 */
export const waitForOnline = (timeoutMilliseconds: number): Promise<void> => {
  if (online) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const finish = () => {
      clearTimeout(timeoutId);
      offWindowEvent("online", finish);
      resolve();
    };

    timeoutId = setTimeout(finish, timeoutMilliseconds);
    onWindowEvent("online", finish);
  });
};

export const initReconnect = () => {
  // revalidate on reconnected
  const onOnline = () => {
    online = true;
  };
  // nothing to revalidate, just update the status
  const onOffline = () => {
    online = false;
  };
  onWindowEvent("online", onOnline);
  onWindowEvent("offline", onOffline);
  return () => {
    offWindowEvent("online", onOnline);
    offWindowEvent("offline", onOffline);
  };
};
